import React, { useContext, useEffect, useRef, useState } from 'react';
import { Context } from './global-data-provider';
import api from '../api/api';
import { useAlert } from 'react-alert';
import { MemberContext } from './member-context';

export const PostLgReactionsContext = React.createContext({});

const PostLgReactionsProvider = ({ children }) => {
  const provider = {};
  const { post } = useContext(Context);
  const member = useContext(MemberContext);
  const inputRef = useRef();
  const [moreLoading, setMoreLoading] = useState(false);
  const [commentPage, setCommentPage] = useState(1);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [requestSending, setRequestSending] = useState(false);
  const [comments, setComments] = useState({ list: post.data.comments.list });
  const [newComments, setNewComments] = useState({ list: [] });
  const [hasMore, setHasMore] = useState(post.data.comments.hasMore);
  const [repliesId, setRepliesId] = useState(0);
  const [removeCount, setRemoveCount] = useState(0);
  const [showLoginRequired, setShowLoginRequired] = useState(false);
  const alert = useAlert();
  const inputSimulator = document.createElement('textarea');

  provider.comments = comments;
  provider.newComments = newComments;
  provider.removeCount = removeCount;
  provider.hasMore = hasMore;
  provider.loadMoreComment = () => {
    loadMoreComment();
  };
  provider.moreLoading = moreLoading;
  provider.repliesId = repliesId;
  provider.addReplies = (id) => {
    setRepliesId(id);
    inputRef.current.focus();
  };
  provider.removeComment = (id) => {
    removeComment(id);
  };
  provider.removeReplies = (id) => {
    removeReplies(id);
  };
  provider.cancelReplies = () => {
    setRepliesId(0);
  };

  provider.inputRef = inputRef;
  provider.showLoginRequired = showLoginRequired;
  provider.setShowLoginRequired = (value) => {
    setShowLoginRequired(value);
  };
  provider.formSubmit = (e) => {
    e.preventDefault();
    formSubmit();
  };

  provider.submitDisabled = submitDisabled;
  provider.setSubmitDisabled = (value) => {
    setSubmitDisabled(value);
  };

  provider.requestSending = requestSending;

  provider.init = () => {
    init();
  };

  const init = () => {
    if (inputRef.current) {
      inputRef.current.addEventListener('keydown', inputOnChange);
      inputRef.current.addEventListener('input', inputOnChange);
      inputRef.current.addEventListener('change', inputOnChange);
      inputSimulator.style.cssText = `width: ${inputRef.current.offsetWidth}px; height: 19px; padding: 0;`;
      inputSimulator.classList.add('simulator-textarea');
      document.body.appendChild(inputSimulator);
    }
  };

  useEffect(() => {
    init();

    return () => {
      inputSimulator.remove();
    };
  }, []);

  const inputOnChange = (e) => {
    setTimeout(() => {
      inputSimulator.value = inputRef.current.value;

      const height = inputSimulator.scrollHeight < 80 ? inputSimulator.scrollHeight : 80;
      inputRef.current.style.height = height + 'px';

      if (height === 80) {
        inputRef.current.classList.remove('o-hidden');
        inputRef.current.classList.add('o-auto');
      } else {
        inputRef.current.classList.remove('o-auto');
        inputRef.current.classList.add('o-hidden');
      }

      if (inputRef.current.value.length > 0) {
        setSubmitDisabled(false);
      } else {
        setSubmitDisabled(true);
      }
    }, 10);
  };

  const formSubmit = () => {
    const value = inputRef.current.value;

    if (value.length <= 4200000000) {
      setSubmitDisabled(true);
      setRequestSending(true);

      const data = {
        post_id: post.data.id,
        content: value,
      };
      if (repliesId) {
        data.replies = repliesId;
      }

      api.comment
        .add(data)
        .then((response) => {
          const status = response && response.status;
          const message = response && response.message;
          const id = response && response.id;

          if (status === 'ok') {
            if (repliesId) {
              const patchReply = (list) =>
                list.map((item) => (String(item.id) === String(repliesId) ? { ...item, replies: value } : item));

              setComments((prevData) => ({
                ...prevData,
                list: patchReply(prevData.list),
              }));
              setNewComments((prevState) => ({
                ...prevState,
                list: patchReply(prevState.list),
              }));

              setRepliesId(0);
            } else if (id) {
              const created = {
                id: id,
                content: value,
                replies: null,
                member_id: member.auth.value ? member.info.id : null,
                fullname: member.auth.value ? member.info.fullname : null,
                avatar: member.auth.value ? member.info.avatar : null,
              };
              setNewComments((prevState) => ({
                ...prevState,
                list: [created, ...prevState.list],
              }));
            }

            inputRef.current.value = null;
          } else {
            alert.error(Array.isArray(message) ? message.join(' ') : message || 'ارسال نظر با خطا مواجه شد');
            setSubmitDisabled(false);
          }

          setRequestSending(false);
        })
        .catch(() => {
          alert.error('ارسال نظر با خطا مواجه شد');
          setSubmitDisabled(false);
          setRequestSending(false);
        });
    } else {
      alert.error(
        <>
          <p>نظر شما نباید بیشتر از 4200000000 کاراکتر باشد.</p>
          <p>طول فعلی : {value.length}</p>
        </>,
      );
    }
  };

  const removeComment = (id) => {
    const callBack = (response) => {
      const { status, message } = response;

      if (status === 'ok') {
        let removedFromCommentList = false;
        const list = comments.list;
        for (let i = 0; i < list.length; i++) {
          if (list[i].id === id) {
            list.splice(i, 1);
            setComments((prevData) => {
              const update = {
                list: list,
              };

              return { ...prevData, ...update };
            });
            setRemoveCount(removeCount + 1);
            removedFromCommentList = true;
            break;
          }
        }

        if (removedFromCommentList === false) {
          const _list = newComments.list;
          for (let i = 0; i < _list.length; i++) {
            if (_list[i].id === id) {
              _list.splice(i, 1);
              setNewComments((prevData) => {
                const update = {
                  list: _list,
                };

                return { ...prevData, ...update };
              });
              break;
            }
          }
        }
      } else if (status === 'error') {
        alert.error(message);
      }
    };

    if (member.auth.value) {
      api.member.removeComment({ id }).then((response) => {
        callBack(response);
      });
    } else {
      api.comment.remove({ id }).then((response) => {
        callBack(response);
      });
    }
  };

  const removeReplies = (id) => {
    api.comment.removeReplies({ id }).then((response) => {
      const { status, message } = response;

      if (status === 'ok') {
        setComments((prevData) => {
          const list = prevData.list;
          for (let i = 0; i < list.length; i++) {
            if (list[i].id === id) {
              list[i].replies = null;
              break;
            }
          }
          const update = {
            list: list,
          };

          return { ...prevData, ...update };
        });
      } else if (status === 'error') {
        alert.error(message);
      }
    });
  };

  const loadMoreComment = () => {
    setMoreLoading(true);

    const data = {
      post_id: post.data.id,
      p: commentPage,
      shift: newComments.list.length,
      unshift: removeCount,
    };

    api.comment.get(data).then((response) => {
      const { status, message, list, hasMore } = response;

      if (status === 'error') {
        alert.error(message);
      } else {
        if (list && list.length > 0) {
          const newList = comments.list.concat(list);
          setComments((prevComments) => {
            const update = {
              list: newList,
            };
            return { ...prevComments, ...update };
          });
        }
      }

      setCommentPage(commentPage + 1);
      setHasMore(hasMore);
      setMoreLoading(false);
    });
  };

  return <PostLgReactionsContext.Provider value={[provider]}>{children}</PostLgReactionsContext.Provider>;
};

export default PostLgReactionsProvider;
